import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import FileUpload from "@/components/FileUpload";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import AdminCard from "./AdminCard";
import EmailProviderCard from "./EmailProviderCard";

const INITIAL_FORM = {
    facebook_group_url: "",
    whatsapp_group_url: "",
    resident_panel_url: "",
    voting_guide_title: "",
    voting_guide_content: "",
    zarzad_name: "",
    zarzad_email: "",
    zarzad_phone: "",
    admin_osiedla_name: "",
    admin_osiedla_email: "",
    admin_osiedla_phone: "",
    hero_title_line_1: "",
    hero_title_line_2: "",
    hero_subtitle: "",
    hero_image_url: "",
    stats_years_value: "",
    stats_years_label: "",
    stats_buildings_value: "",
    stats_buildings_label: "",
    ad_enabled: false,
    ad_title: "",
    ad_subtitle: "",
    ad_image_url: "",
    ad_link: "",
    ad_cta: "",
    ad_badge: "",
    resend_api_key: "",
    resend_from_email: "",
    resend_from_name: "",
    mail_provider: "resend",
    ses_access_key_id: "",
    ses_secret_access_key: "",
    ses_region: "eu-central-1",
    ses_configuration_set: "",
    footer_author: "",
    footer_copyright: "",
    google_analytics_id: "",
    site_admin_email: "",
    privacy_policy_title: "",
    privacy_policy_subtitle: "",
    privacy_policy_html: "",
    report_email_1: "",
    report_email_2: "",
    report_email_3: "",
    report_email_4: "",
    contact_email_1: "",
    contact_email_2: "",
    contact_email_3: "",
    contact_email_4: "",
    report_intro_html: "",
    // SEO
    seo_site_name: "",
    seo_default_title: "",
    seo_title_template: "",
    seo_default_description: "",
    seo_keywords: "",
    seo_og_image_url: "",
    seo_twitter_handle: "",
    seo_favicon_url: "",
    seo_canonical_base: "",
    seo_robots: "index,follow",
    seo_locale: "pl_PL",
};

export default function UstawieniaAdmin() {
    const [form, setForm] = useState(INITIAL_FORM);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/settings/admin").then((r) => {
            setForm((prev) => ({ ...prev, ...(r.data || {}) }));
            setLoaded(true);
        }).catch(() => setLoaded(true));
    }, []);

    const save = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.put("/settings", form);
            toast.success("Ustawienia zapisane.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    if (!loaded) return <p className="text-gray-500">Wczytywanie...</p>;

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <AdminCard className="lg:col-span-2">
                <h3 className="font-serif text-xl text-[#092E22] mb-1">SEO – pozycjonowanie i meta</h3>
                <p className="text-sm text-gray-500 mb-5">
                    Tytuły, opisy i obrazy podpowiadane wyszukiwarkom (Google, Bing) oraz portalom społecznościowym (Facebook, X/Twitter).
                    Mapa strony i robots.txt są generowane automatycznie na podstawie tych pól.
                </p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-seo-form">
                    <div className="grid md:grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Nazwa serwisu</Label>
                            <Input value={form.seo_site_name} onChange={setField("seo_site_name")} placeholder="Dąbrówka Wiślana" data-testid="seo-site-name" />
                        </div>
                        <div className="space-y-2">
                            <Label>Język/kraj (locale)</Label>
                            <Input value={form.seo_locale} onChange={setField("seo_locale")} placeholder="pl_PL" data-testid="seo-locale" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Domyślny tytuł strony głównej</Label>
                        <Input value={form.seo_default_title} onChange={setField("seo_default_title")} placeholder="Dąbrówka Wiślana – Wspólnota Mieszkaniowa..." data-testid="seo-default-title" />
                    </div>
                    <div className="space-y-2">
                        <Label>Szablon tytułu podstron (użyj %s w miejscu nazwy podstrony)</Label>
                        <Input value={form.seo_title_template} onChange={setField("seo_title_template")} placeholder="%s · Dąbrówka Wiślana" data-testid="seo-title-template" />
                        <p className="text-[11px] text-gray-400">Przykład: <code>%s · Dąbrówka Wiślana</code> → dla wiadomości „Spotkanie": „Spotkanie · Dąbrówka Wiślana"</p>
                    </div>
                    <div className="space-y-2">
                        <Label>Domyślny opis (max. 160 znaków)</Label>
                        <Textarea rows={3} maxLength={300} value={form.seo_default_description} onChange={setField("seo_default_description")} placeholder="Oficjalna strona wspólnoty mieszkaniowej..." data-testid="seo-default-description" />
                    </div>
                    <div className="space-y-2">
                        <Label>Słowa kluczowe (rozdziel przecinkami)</Label>
                        <Input value={form.seo_keywords} onChange={setField("seo_keywords")} placeholder="Dąbrówka Wiślana, Aluzyjna 25, wspólnota, Białołęka, Warszawa" data-testid="seo-keywords" />
                    </div>

                    <div className="space-y-2">
                        <Label>Obraz Open Graph (1200×630 px) – wyświetlany przy udostępnianiu w social media</Label>
                        <FileUpload
                            accept="image/*"
                            label="Wgraj obraz OG"
                            testid="seo-og-image-upload"
                            onUploaded={(d) => setForm((f) => ({ ...f, seo_og_image_url: d.url }))}
                        />
                        <Input value={form.seo_og_image_url} onChange={setField("seo_og_image_url")} placeholder="https://... lub /api/files/xxx.jpg" data-testid="seo-og-image" />
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Favicon (URL)</Label>
                            <FileUpload accept="image/*" label="Wgraj favicon" testid="seo-favicon-upload" onUploaded={(d) => setForm((f) => ({ ...f, seo_favicon_url: d.url }))} />
                            <Input value={form.seo_favicon_url} onChange={setField("seo_favicon_url")} placeholder="/api/files/favicon.png" data-testid="seo-favicon" />
                        </div>
                        <div className="space-y-2">
                            <Label>Twitter handle (z @)</Label>
                            <Input value={form.seo_twitter_handle} onChange={setField("seo_twitter_handle")} placeholder="@aluzyjna25" data-testid="seo-twitter" />
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Bazowy URL kanoniczny</Label>
                            <Input value={form.seo_canonical_base} onChange={setField("seo_canonical_base")} placeholder="https://aluzyjna25cdef.pl" data-testid="seo-canonical-base" />
                            <p className="text-[11px] text-gray-400">Używane w robots.txt i sitemap.xml. Pozostaw puste, by używać aktualnego hosta.</p>
                        </div>
                        <div className="space-y-2">
                            <Label>Robots (index/noindex)</Label>
                            <Input value={form.seo_robots} onChange={setField("seo_robots")} placeholder="index,follow" data-testid="seo-robots" />
                            <p className="text-[11px] text-gray-400">Na czas prac zmień na <code>noindex,nofollow</code> – wyszukiwarki przestaną indeksować stronę.</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-50">
                        <Button type="submit" disabled={loading} data-testid="admin-settings-seo-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                            {loading ? "Zapisywanie..." : "Zapisz ustawienia SEO"}
                        </Button>
                        <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="text-xs text-[#0D4F3C] hover:underline">Otwórz sitemap.xml</a>
                        <a href="/robots.txt" target="_blank" rel="noreferrer" className="text-xs text-[#0D4F3C] hover:underline">Otwórz robots.txt</a>
                    </div>
                </form>
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Panel mieszkańca</h3>
                <p className="text-sm text-gray-500 mb-5">Link do zewnętrznego portalu mieszkańca (np. system rozliczeń, dokumenty). Jeśli puste — przycisk na stronie głównej pokaże komunikat „Wkrótce dostępny".</p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-resident-form">
                    <div className="space-y-2">
                        <Label>URL panelu mieszkańca</Label>
                        <Input
                            type="url"
                            value={form.resident_panel_url}
                            onChange={setField("resident_panel_url")}
                            placeholder="https://mieszkaniec.aluzyjna25cdef.pl"
                            data-testid="admin-settings-resident-url"
                        />
                        <p className="text-[11px] text-gray-500">Pozostaw puste, żeby wyświetlać informację „Wkrótce" po kliknięciu przycisku.</p>
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-settings-resident-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz ustawienia"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Linki do grup społecznościowych</h3>
                <p className="text-sm text-gray-500 mb-5">Wyświetlane w sekcji „Grupa osiedlowa" na stronie głównej.</p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-social-form">
                    <div className="space-y-2">
                        <Label>Link do grupy na Facebooku</Label>
                        <Input type="url" value={form.facebook_group_url} onChange={setField("facebook_group_url")} placeholder="https://facebook.com/groups/..." data-testid="admin-settings-facebook" />
                    </div>
                    <div className="space-y-2">
                        <Label>Link do grupy WhatsApp</Label>
                        <Input type="url" value={form.whatsapp_group_url} onChange={setField("whatsapp_group_url")} placeholder="https://chat.whatsapp.com/..." data-testid="admin-settings-whatsapp" />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-settings-social-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz ustawienia"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Instrukcja „Jak głosować"</h3>
                <p className="text-sm text-gray-500 mb-5">Treść wyświetlana na stronie <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">/jak-glosowac</code>, do której prowadzi link przy każdej uchwale.</p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-voting-form">
                    <div className="space-y-2">
                        <Label>Tytuł strony</Label>
                        <Input value={form.voting_guide_title} onChange={setField("voting_guide_title")} placeholder="Jak głosować nad uchwałami wspólnoty" data-testid="admin-settings-voting-title" />
                    </div>
                    <div className="space-y-2">
                        <Label>Treść instrukcji (edytor wzbogacony – pogrubienie, kolory, listy)</Label>
                        <RichTextEditor
                            value={form.voting_guide_content}
                            onChange={(v) => setForm({ ...form, voting_guide_content: v })}
                            placeholder="Wyjaśnij krok po kroku, jak mieszkańcy mogą oddać głos..."
                            testid="admin-settings-voting-content"
                            allowImages={false}
                        />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-settings-voting-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz instrukcję"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Kontakt do zarządu wspólnoty</h3>
                <p className="text-sm text-gray-500 mb-5">Wyświetlany w sekcji „Kontakt" na stronie głównej.</p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-zarzad-form">
                    <div className="space-y-2">
                        <Label>Imię i nazwisko / nazwa</Label>
                        <Input value={form.zarzad_name} onChange={setField("zarzad_name")} placeholder="np. Jan Kowalski (prezes zarządu)" data-testid="admin-settings-zarzad-name" />
                    </div>
                    <div className="space-y-2">
                        <Label>E-mail</Label>
                        <Input type="email" value={form.zarzad_email} onChange={setField("zarzad_email")} placeholder="zarzad@aluzyjna25cdef.pl" data-testid="admin-settings-zarzad-email" />
                    </div>
                    <div className="space-y-2">
                        <Label>Telefon</Label>
                        <Input value={form.zarzad_phone} onChange={setField("zarzad_phone")} placeholder="+48 ..." data-testid="admin-settings-zarzad-phone" />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-settings-zarzad-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz dane zarządu"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Kontakt do administratora osiedla</h3>
                <p className="text-sm text-gray-500 mb-5">Firma / osoba zarządzająca nieruchomością. Wyświetlany w sekcji „Kontakt".</p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-osiedle-form">
                    <div className="space-y-2">
                        <Label>Nazwa firmy / imię i nazwisko</Label>
                        <Input value={form.admin_osiedla_name} onChange={setField("admin_osiedla_name")} placeholder="np. ABC Zarządzanie Sp. z o.o." data-testid="admin-settings-osiedle-name" />
                    </div>
                    <div className="space-y-2">
                        <Label>E-mail</Label>
                        <Input type="email" value={form.admin_osiedla_email} onChange={setField("admin_osiedla_email")} placeholder="biuro@firma.pl" data-testid="admin-settings-osiedle-email" />
                    </div>
                    <div className="space-y-2">
                        <Label>Telefon</Label>
                        <Input value={form.admin_osiedla_phone} onChange={setField("admin_osiedla_phone")} placeholder="+48 ..." data-testid="admin-settings-osiedle-phone" />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-settings-osiedle-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz dane administratora osiedla"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Administrator strony (adres e-mail)</h3>
                <p className="text-sm text-gray-500 mb-5">Adres wyświetlany w sekcji „Kontakt" pod etykietą „Administrator strony", w stopce oraz w polityce prywatności.</p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-site-admin-form">
                    <div className="space-y-2">
                        <Label>E-mail administratora strony</Label>
                        <Input
                            type="email"
                            value={form.site_admin_email}
                            onChange={setField("site_admin_email")}
                            placeholder="portal@aluzyjna25cdef.pl"
                            data-testid="admin-settings-site-admin-email"
                        />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-settings-site-admin-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz adres administratora"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard className="lg:col-span-2">
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Sekcja Hero (nagłówek strony głównej)</h3>
                <p className="text-sm text-gray-500 mb-5">Treść wyświetlana na początku strony – tytuł i podtytuł.</p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-hero-form">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Tytuł – linia 1</Label>
                            <Input value={form.hero_title_line_1} onChange={setField("hero_title_line_1")} data-testid="admin-settings-hero-line1" />
                        </div>
                        <div className="space-y-2">
                            <Label>Tytuł – linia 2 (ostatnie słowo w kolorze złota)</Label>
                            <Input value={form.hero_title_line_2} onChange={setField("hero_title_line_2")} data-testid="admin-settings-hero-line2" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Podtytuł</Label>
                        <Textarea rows={3} value={form.hero_subtitle} onChange={setField("hero_subtitle")} data-testid="admin-settings-hero-subtitle" />
                    </div>
                    <div className="space-y-2">
                        <Label>Zdjęcie tła Hero</Label>
                        <FileUpload
                            accept=".jpg,.jpeg,.png,.webp"
                            label="Wgraj zdjęcie z komputera"
                            testid="admin-settings-hero-image-upload"
                            onUploaded={(data) => setForm((f) => ({ ...f, hero_image_url: data.url }))}
                        />
                        {form.hero_image_url && (
                            <div className="flex items-center gap-3 mt-2">
                                <img
                                    src={form.hero_image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.hero_image_url}` : form.hero_image_url}
                                    alt="Podgląd hero"
                                    className="w-32 h-20 object-cover rounded-xl border border-gray-200"
                                />
                                <Input value={form.hero_image_url} onChange={setField("hero_image_url")} placeholder="...lub wklej URL" className="text-xs" data-testid="admin-settings-hero-image-url" />
                            </div>
                        )}
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-settings-hero-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz Hero"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard className="lg:col-span-2">
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Statystyki na stronie głównej</h3>
                <p className="text-sm text-gray-500 mb-5">Wartości „Lat wspólnoty" i „Budynki" są edytowalne. Pozostałe liczby (komunikaty, wiadomości, uchwały, subskrybenci) liczone są automatycznie.</p>
                <form onSubmit={save} className="grid md:grid-cols-2 gap-4" data-testid="admin-settings-stats-form">
                    <div className="space-y-2">
                        <Label>Liczba lat – wartość</Label>
                        <Input value={form.stats_years_value} onChange={setField("stats_years_value")} placeholder="5" data-testid="admin-settings-stats-years-value" />
                    </div>
                    <div className="space-y-2">
                        <Label>Liczba lat – etykieta</Label>
                        <Input value={form.stats_years_label} onChange={setField("stats_years_label")} placeholder="Lat wspólnoty" data-testid="admin-settings-stats-years-label" />
                    </div>
                    <div className="space-y-2">
                        <Label>Budynki – wartość</Label>
                        <Input value={form.stats_buildings_value} onChange={setField("stats_buildings_value")} placeholder="4" data-testid="admin-settings-stats-buildings-value" />
                    </div>
                    <div className="space-y-2">
                        <Label>Budynki – etykieta</Label>
                        <Input value={form.stats_buildings_label} onChange={setField("stats_buildings_label")} placeholder="Budynki C/D/E/F" data-testid="admin-settings-stats-buildings-label" />
                    </div>
                    <div className="md:col-span-2">
                        <Button type="submit" disabled={loading} data-testid="admin-settings-stats-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                            {loading ? "Zapisywanie..." : "Zapisz statystyki"}
                        </Button>
                    </div>
                </form>
            </AdminCard>

            <AdminCard className="lg:col-span-2">
                <div className="flex items-start justify-between mb-1 gap-4">
                    <h3 className="font-serif text-xl text-[#092E22]">Reklama / sponsorowany baner</h3>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <Switch checked={!!form.ad_enabled} onCheckedChange={(v) => setForm({ ...form, ad_enabled: v })} data-testid="admin-settings-ad-enabled" />
                        <span className="text-[#092E22]">{form.ad_enabled ? "Włączona" : "Wyłączona"}</span>
                    </label>
                </div>
                <p className="text-sm text-gray-500 mb-5">Baner wyświetlany na stronie głównej pomiędzy „Wiadomości" a „Uchwały". Włącz suwakiem, aby pokazać.</p>
                <form onSubmit={save} className="grid md:grid-cols-2 gap-4" data-testid="admin-settings-ad-form">
                    <div className="space-y-2">
                        <Label>Etykieta (np. „Reklama", „Partner")</Label>
                        <Input value={form.ad_badge} onChange={setField("ad_badge")} placeholder="Reklama" data-testid="admin-settings-ad-badge" />
                    </div>
                    <div className="space-y-2">
                        <Label>Link docelowy</Label>
                        <Input type="url" value={form.ad_link} onChange={setField("ad_link")} placeholder="https://..." data-testid="admin-settings-ad-link" />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                        <Label>Tytuł reklamy</Label>
                        <Input value={form.ad_title} onChange={setField("ad_title")} placeholder="Tytuł banera" data-testid="admin-settings-ad-title" />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                        <Label>Treść / podtytuł</Label>
                        <Textarea rows={3} value={form.ad_subtitle} onChange={setField("ad_subtitle")} placeholder="Krótka treść reklamy..." data-testid="admin-settings-ad-subtitle" />
                    </div>
                    <div className="space-y-2">
                        <Label>URL obrazka (opcjonalnie)</Label>
                        <Input type="url" value={form.ad_image_url} onChange={setField("ad_image_url")} placeholder="https://..." data-testid="admin-settings-ad-image" />
                    </div>
                    <div className="space-y-2">
                        <Label>Tekst przycisku CTA</Label>
                        <Input value={form.ad_cta} onChange={setField("ad_cta")} placeholder="Dowiedz się więcej" data-testid="admin-settings-ad-cta" />
                    </div>
                    <div className="md:col-span-2">
                        <Button type="submit" disabled={loading} data-testid="admin-settings-ad-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                            {loading ? "Zapisywanie..." : "Zapisz reklamę"}
                        </Button>
                    </div>
                </form>
            </AdminCard>

            <EmailProviderCard form={form} setField={setField} setForm={setForm} save={save} loading={loading} testButton={<ResendTestButton />} />

            <AdminCard className="lg:col-span-2">
                <h3 className="font-serif text-xl text-[#092E22] mb-1">E-maile odbiorców wiadomości z formularza kontaktowego</h3>
                <p className="text-sm text-gray-500 mb-5">
                    Maksymalnie 4 adresy, na które będą wysyłane wiadomości z formularza „Kontakt" na stronie głównej.
                    Wysyłka działa, gdy skonfigurowany jest dostawca e-mail powyżej. Wszystkie wiadomości zawsze zapisują się też w panelu (Wiadomości kontaktowe).
                </p>
                <form onSubmit={save} className="grid md:grid-cols-2 gap-4" data-testid="admin-settings-contacts-form">
                    {[1, 2, 3, 4].map((n) => (
                        <div key={n} className="space-y-2">
                            <Label>Odbiorca #{n}</Label>
                            <Input
                                type="email"
                                value={form[`contact_email_${n}`]}
                                onChange={setField(`contact_email_${n}`)}
                                placeholder={`kontakt-${n}@aluzyjna25cdef.pl`}
                                data-testid={`admin-settings-contact-email-${n}`}
                            />
                        </div>
                    ))}
                    <div className="md:col-span-2">
                        <Button type="submit" disabled={loading} data-testid="admin-settings-contacts-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                            {loading ? "Zapisywanie..." : "Zapisz odbiorców"}
                        </Button>
                    </div>
                </form>
            </AdminCard>

            <AdminCard className="lg:col-span-2">
                <h3 className="font-serif text-xl text-[#092E22] mb-1">E-maile odbiorców zgłoszeń usterek</h3>
                <p className="text-sm text-gray-500 mb-5">
                    Maksymalnie 4 adresy, na które będą wysyłane zgłoszenia z formularza „Zgłoś usterkę".
                    Wysyłka działa, gdy skonfigurowany jest dostawca e-mail powyżej.
                </p>
                <form onSubmit={save} className="grid md:grid-cols-2 gap-4" data-testid="admin-settings-reports-form">
                    {[1, 2, 3, 4].map((n) => (
                        <div key={n} className="space-y-2">
                            <Label>Odbiorca #{n}</Label>
                            <Input
                                type="email"
                                value={form[`report_email_${n}`]}
                                onChange={setField(`report_email_${n}`)}
                                placeholder={`email-${n}@aluzyjna25cdef.pl`}
                                data-testid={`admin-settings-report-email-${n}`}
                            />
                        </div>
                    ))}
                    <div className="md:col-span-2">
                        <Button type="submit" disabled={loading} data-testid="admin-settings-reports-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                            {loading ? "Zapisywanie..." : "Zapisz odbiorców"}
                        </Button>
                    </div>
                </form>
            </AdminCard>

            <AdminCard className="lg:col-span-2">
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Treść strony „Zgłoś usterkę"</h3>
                <p className="text-sm text-gray-500 mb-5">Tekst wyświetlany nad formularzem zgłoszenia (jak działa proces, do kogo trafia zgłoszenie).</p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-report-intro-form">
                    <RichTextEditor
                        value={form.report_intro_html}
                        onChange={(v) => setForm({ ...form, report_intro_html: v })}
                        placeholder="Opisz, jak działa zgłaszanie usterek..."
                        testid="admin-settings-report-intro"
                        allowImages={false}
                    />
                    <Button type="submit" disabled={loading} data-testid="admin-settings-report-intro-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz treść"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard className="lg:col-span-2">
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Polityka prywatności</h3>
                <p className="text-sm text-gray-500 mb-5">
                    Pełna treść strony <code className="bg-gray-100 px-1 rounded text-xs">/polityka-prywatnosci</code>.
                    Możesz dowolnie edytować nagłówki, punkty i akapity. W razie potrzeby zachowaj numerację (1, 2, 3...).
                </p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-privacy-form">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Tytuł strony</Label>
                            <Input
                                value={form.privacy_policy_title}
                                onChange={setField("privacy_policy_title")}
                                placeholder="Polityka prywatności"
                                data-testid="admin-settings-privacy-title"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Podtytuł</Label>
                            <Input
                                value={form.privacy_policy_subtitle}
                                onChange={setField("privacy_policy_subtitle")}
                                placeholder="Wspólnota Mieszkaniowa Dąbrówka Wiślana – Aluzyjna 25 C/D/E/F"
                                data-testid="admin-settings-privacy-subtitle"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Treść (edytor: pogrubienie, listy, linki, nagłówki)</Label>
                        <RichTextEditor
                            value={form.privacy_policy_html}
                            onChange={(v) => setForm({ ...form, privacy_policy_html: v })}
                            placeholder="Treść polityki prywatności..."
                            testid="admin-settings-privacy-content"
                            allowImages={false}
                        />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-settings-privacy-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz politykę prywatności"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard className="lg:col-span-2">
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Stopka – tekst praw autorskich</h3>
                <p className="text-sm text-gray-500 mb-5">Tekst po lewej stronie stopki. Możesz użyć znacznika <code className="bg-gray-100 px-1 rounded text-xs">{`{year}`}</code>, aby wstawić bieżący rok automatycznie.</p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-footer-copyright-form">
                    <div className="space-y-2">
                        <Label>Tekst praw autorskich</Label>
                        <Input value={form.footer_copyright} onChange={setField("footer_copyright")} placeholder="© {year} Wspólnota Mieszkaniowa Dąbrówka Wiślana. Wszystkie prawa zastrzeżone." data-testid="admin-settings-footer-copyright" />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-settings-footer-copyright-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz prawa autorskie"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard className="lg:col-span-2">
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Stopka – autor / podpis</h3>
                <p className="text-sm text-gray-500 mb-5">Tekst wyświetlany w stopce (prawa strona). Można używać HTML, np. <code className="bg-gray-100 px-1 rounded text-xs">&lt;span class="text-[#D4A537]"&gt;tekst&lt;/span&gt;</code>.</p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-footer-form">
                    <div className="space-y-2">
                        <Label>Tekst stopki</Label>
                        <Input value={form.footer_author} onChange={setField("footer_author")} placeholder="Projekt i wykonanie: Michał Skarżyński" data-testid="admin-settings-footer-author" />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-settings-footer-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz stopkę"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard className="lg:col-span-2">
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Google Analytics</h3>
                <p className="text-sm text-gray-500 mb-5">
                    Wklej swój identyfikator pomiaru (Measurement ID) z Google Analytics 4 – znajdziesz go w <a href="https://analytics.google.com" target="_blank" rel="noreferrer" className="text-[#0D4F3C] underline">analytics.google.com</a> → Administracja → Strumienie danych. Format: <code className="bg-gray-100 px-1 rounded text-xs">G-XXXXXXXXXX</code>. Pozostaw puste, aby wyłączyć śledzenie.
                </p>
                <form onSubmit={save} className="space-y-4" data-testid="admin-settings-ga-form">
                    <div className="space-y-2">
                        <Label>Measurement ID</Label>
                        <Input value={form.google_analytics_id} onChange={setField("google_analytics_id")} placeholder="G-XXXXXXXXXX" data-testid="admin-settings-ga-id" />
                        <p className="text-[11px] text-gray-500">Po zapisaniu odśwież publiczną stronę – skrypt gtag.js zostanie automatycznie wstrzyknięty na każdej podstronie. Adresy IP są anonimizowane.</p>
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-settings-ga-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : "Zapisz Google Analytics"}
                    </Button>
                </form>
            </AdminCard>
        </div>
    );
}

function ResendTestButton() {
    const [open, setOpen] = useState(false);
    const [to, setTo] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        setBusy(true);
        setResult(null);
        try {
            const r = await api.post("/settings/resend/test", { to: to.trim() || null });
            setResult(r.data);
            if (r.data?.ok) {
                toast.success("Wiadomość testowa wysłana. Sprawdź skrzynkę odbiorcy.");
            } else if (r.data?.problems?.length) {
                toast.error("Konfiguracja niekompletna – zobacz szczegóły.");
            } else {
                toast.error("Dostawca odrzucił wysyłkę – zobacz szczegóły.");
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    return (
        <>
            <Button
                type="button"
                variant="outline"
                onClick={() => { setOpen(true); setResult(null); }}
                data-testid="admin-settings-resend-test-open"
                className="rounded-full"
            >
                ✉️ Testuj wysyłkę
            </Button>
            {open && (
                <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
                    <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-7 my-8" onClick={(e) => e.stopPropagation()} data-testid="admin-settings-resend-test-modal">
                        <h3 className="font-serif text-2xl text-[#092E22] mb-1">Test wysyłki e-mail</h3>
                        <p className="text-sm text-gray-500 mb-5">
                            Wyśle testową wiadomość przez aktywnego dostawcę (Resend lub Amazon SES), używając ZAPISANEJ konfiguracji. Jeśli właśnie coś zmieniłeś – najpierw kliknij „Zapisz”.
                        </p>
                        <div className="space-y-3">
                            <div className="space-y-2">
                                <Label>Adres odbiorcy testu (zostaw puste, aby wysłać do wszystkich odbiorców zgłoszeń)</Label>
                                <Input
                                    type="email"
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                    placeholder="ja@mojadomena.pl"
                                    data-testid="admin-settings-resend-test-to"
                                />
                            </div>
                            <Button onClick={run} disabled={busy} data-testid="admin-settings-resend-test-run" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                                {busy ? "Wysyłanie..." : "Wyślij test"}
                            </Button>

                            {result && (
                                <div className={`mt-2 rounded-xl border p-4 text-sm ${result.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`} data-testid="admin-settings-resend-test-result">
                                    {result.ok && (
                                        <p className="font-semibold">✅ Wysłano pomyślnie do {result.recipients?.length || 0} odbiorcy(ów).</p>
                                    )}
                                    {result.problems?.length > 0 && (
                                        <>
                                            <p className="font-semibold mb-1">Problemy konfiguracji:</p>
                                            <ul className="list-disc pl-5 space-y-1">
                                                {result.problems.map((p, i) => <li key={i}>{p}</li>)}
                                            </ul>
                                        </>
                                    )}
                                    {result.result?.errors?.length > 0 && (
                                        <>
                                            <p className="font-semibold mt-2 mb-1">Odpowiedzi dostawcy:</p>
                                            <ul className="list-disc pl-5 space-y-1 break-words">
                                                {result.result.errors.map((e, i) => <li key={i} className="font-mono text-xs">{e}</li>)}
                                            </ul>
                                        </>
                                    )}
                                    {result.from && (
                                        <p className="mt-2 text-xs opacity-80">Nadawca: <code className="font-mono">{result.from}</code></p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="mt-5 flex justify-end">
                            <Button variant="outline" onClick={() => setOpen(false)} data-testid="admin-settings-resend-test-close" className="rounded-full">Zamknij</Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
